import { assertDefined } from '@utils';
import { LocaleId } from './LocaleId';
import { lookupString } from './lookupString';
import { PlatformId } from './PlatformId';
import type { StringId } from './StringId';

export function localizedKey(
  str: StringId,
  platform: PlatformId,
  locale?: LocaleId
): string {
  const canonicalKey = canonicalKeyNames[str.toLowerCase()] ?? str;
  const baseDisplay = baseKeyDisplayStrings[canonicalKey] ?? canonicalKey;
  const platformDisplay = platformKeyDisplayStrings[platform][baseDisplay] ?? baseDisplay;
  let localized = lookupString(platformDisplay as StringId, locale);
  if (localized === undefined) {
    localized = platformDisplay;
  }
  assertDefined(localized);
  return localized;
}

export function localizedKeyTrigger(
  keyTrigger: string,
  platform: PlatformId,
  locale?: LocaleId
): string {
  const chords = keyTrigger.split(' ');
  return chords
    .map(chord => {
      const keys = chord.split('+');
      return keys.map(key => localizedKey(key as StringId, platform, locale)).join(' ');
    })
    .join(' ');
}

export enum CanonicalKey {
  SHIFT = 'Shift',
  ALT = 'Alt',
  CONTROL = 'Control',
  META = 'Meta',
  BACKSPACE = 'Backspace',
  TAB = 'Tab',
  ENTER = 'Enter',
  ESCAPE = 'Escape',
  SPACE = 'Space',
  ARROW_LEFT = 'ArrowLeft',
  ARROW_UP = 'ArrowUp',
  ARROW_RIGHT = 'ArrowRight',
  ARROW_DOWN = 'ArrowDown'
}

const canonicalKeyNames: Record<string, CanonicalKey> = {
  shift: CanonicalKey.SHIFT,

  alt: CanonicalKey.ALT,
  option: CanonicalKey.ALT,

  control: CanonicalKey.CONTROL,
  ctrl: CanonicalKey.CONTROL,

  meta: CanonicalKey.META,
  command: CanonicalKey.META,
  cmd: CanonicalKey.META,
  win: CanonicalKey.META,
  super: CanonicalKey.META,

  backspace: CanonicalKey.BACKSPACE,
  delete: CanonicalKey.BACKSPACE,

  tab: CanonicalKey.TAB,

  enter: CanonicalKey.ENTER,
  return: CanonicalKey.ENTER,

  escape: CanonicalKey.ESCAPE,
  esc: CanonicalKey.ESCAPE,

  ' ': CanonicalKey.SPACE,
  space: CanonicalKey.SPACE,

  left: CanonicalKey.ARROW_LEFT,
  arrowleft: CanonicalKey.ARROW_LEFT,
  arrow_left: CanonicalKey.ARROW_LEFT,

  up: CanonicalKey.ARROW_UP,
  arrowup: CanonicalKey.ARROW_UP,
  arrow_up: CanonicalKey.ARROW_UP,

  right: CanonicalKey.ARROW_RIGHT,
  arrowright: CanonicalKey.ARROW_RIGHT,
  arrow_right: CanonicalKey.ARROW_RIGHT,

  down: CanonicalKey.ARROW_DOWN,
  arrowdown: CanonicalKey.ARROW_DOWN,
  arrow_down: CanonicalKey.ARROW_DOWN
};

const baseKeyDisplayStrings: Record<string, string> = {
  [CanonicalKey.SHIFT]: 'shiftKey',
  [CanonicalKey.ALT]: 'altKey',
  [CanonicalKey.CONTROL]: 'controlKey',
  [CanonicalKey.META]: 'metaKey',
  [CanonicalKey.BACKSPACE]: 'backspaceKey',
  [CanonicalKey.TAB]: 'tabKey',
  [CanonicalKey.ENTER]: 'enterKey',
  [CanonicalKey.ESCAPE]: 'escapeKey',
  [CanonicalKey.SPACE]: 'spaceKey',
  [CanonicalKey.ARROW_LEFT]: '←',
  [CanonicalKey.ARROW_UP]: '↑',
  [CanonicalKey.ARROW_RIGHT]: '→',
  [CanonicalKey.ARROW_DOWN]: '↓'
};

const platformKeyDisplayStrings: Record<PlatformId, Record<string, string>> = {
  [PlatformId.MAC]: {
    altKey: '⌥',
    controlKey: '⌃',
    metaKey: '⌘'
  },
  [PlatformId.WINDOWS]: {
    metaKey: 'Win'
  },
  [PlatformId.LINUX]: {
    metaKey: 'Super'
  }
};
